import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
} from "reactstrap";

import { Btn, H3, MUIIcons, P } from "../../../../AbstractElements";
import { ALL_USERS_STATUS } from "../../../../Api/constants";
import DetailHeaderContainer from "../../../../CommonElements/DetailHeaderContainer";
import Divider from "../../../../CommonElements/Divider";
import JustifyTabs from "../../../../CommonElements/JustifyTabs";
import CommonModal from "../../../../CommonElements/Modal";
import VerticalDivider from "../../../../CommonElements/VericalDivider";
import NoData from "../../../../container/NoData";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  deleteUserGroup,
  getUserGroupByIdByTab,
} from "../../../../ReduxToolkit/Reducers/UserGroupSlice";
import { getUsersList } from "../../../../ReduxToolkit/Reducers/UserSlice";
import {
  Add,
  AreYouSureDelete,
  Confirm,
  Confirmation,
  DeleteGroup,
  EditDetails,
  NoDataText,
  TotalLearningPlan,
  User,
} from "../../../../utils/Constant";
import { userGroupDetailsTabs } from "../../../../utils/helper/helper";

import LearningPlan from "./LearningPlan";
import UserDetails from "./UserDetails";

const GroupDetails = () => {
  const [activeTab, setActiveTab] = useState("user");
  const [userMap, setUserMap] = useState({});
  const [openDropdown, setOpenDropdown] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);
  const { usersList } = useAppSelector(state => state.user);
  const { userGroupByIdByTab } = useAppSelector(state => state.userGroup);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const group_id = searchParams.get("group_id");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getUsersList({ role: ALL_USERS_STATUS }));
  }, [dispatch]);

  useEffect(() => {
    if (group_id) {
      getByTab();
    }
  }, [group_id, activeTab]);

  const getByTab = async () => {
    await dispatch(getUserGroupByIdByTab({ id: group_id, tab: activeTab }));
  };

  useEffect(() => {
    const userMapObj = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  const handleTabClick = tabId => {
    setActiveTab(tabId);
  };

  const toggleDropdown = () => {
    setOpenDropdown(prevState => !prevState);
  };

  const handleEditDetails = () => {
    navigate(
      `/user-management/edit-new-groups?id=${group_id}&type=basic&page=details`
    );
  };

  const handleConfirmDelete = () => {
    setModalInfo({
      isOpen: true,
      title: AreYouSureDelete,
      callback: () => handleDelete(group_id),
    });
  };

  const handleDelete = async id => {
    await dispatch(deleteUserGroup({ id: id }));
    closeModal();
    navigate("/user-management/user-groups");
  };

  const closeModal = () => {
    setModalInfo(null);
  };

  return (
    <div className="page-body page-body-margin group-details">
      <CommonModal
        sizeTitle={Confirmation}
        modalBodyClassName=""
        isOpen={modalInfo?.isOpen}
        toggle={closeModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={modalInfo?.callback}
        primaryBtnText={Confirm}
      >
        {modalInfo?.title}
      </CommonModal>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <H3 className="fw-bold">{userGroupByIdByTab?.name}</H3>
            <div>
              <Dropdown
                isOpen={openDropdown}
                toggle={() => toggleDropdown()}
                direction="end"
              >
                <DropdownToggle color="" className="three-dots">
                  <MUIIcons
                    iconName="MoreVertOutlined"
                    className="primary-icon-color"
                    size={20}
                  />
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem onClick={handleEditDetails}>
                    {EditDetails}
                  </DropdownItem>
                  <DropdownItem onClick={() => handleConfirmDelete()}>
                    {DeleteGroup}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          <P className="text-muted mt-3 mb-0">
            {userGroupByIdByTab?.description}
          </P>
          <Divider />
          <Row className="mt-3">
            <Col lg="4" sm="12" xs="12">
              <Row>
                <Col sm="5" xs="5" className="d-flex mb-4">
                  <DetailHeaderContainer
                    assign={User}
                    name={userGroupByIdByTab?.total_users || "0"}
                    key={1}
                  />
                </Col>
                <Col sm="1" xs="1" lg="1">
                  <VerticalDivider />
                </Col>
                <Col sm="5" xs="5" className="d-flex mb-4">
                  <DetailHeaderContainer
                    assign={TotalLearningPlan}
                    name={userGroupByIdByTab?.total_plans || "0"}
                    key={2}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="details-tab-width w-100">
          <div className="w-100 d-flex justify-content-between">
            <div className="mt-1">
              <JustifyTabs
                tabs={userGroupDetailsTabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
              />
            </div>
            {activeTab === "user" && (
              <Btn
                icon={
                  <MUIIcons size={16} iconName="AddCircleOutlineOutlined" />
                }
                color="primary"
                onClick={() => {
                  navigate(
                    `/user-management/edit-new-groups?id=${group_id}&type=user&page=details`
                  );
                }}
              >
                {Add}
              </Btn>
            )}
          </div>
        </CardBody>
      </Card>

      {activeTab === "user" && (
        <UserDetails
          users={userGroupByIdByTab?.users}
          userMap={userMap}
          getByTab={getByTab}
          group_id={group_id}
        />
      )}

      {activeTab === "learning_plan" &&
        (userGroupByIdByTab?.learning_plans?.length > 0 ? (
          <LearningPlan learningPlans={userGroupByIdByTab?.learning_plans} />
        ) : (
          <NoData
            svg={"empty-folder-icon"}
            title={NoDataText}
            showCard={false}
          />
        ))}
    </div>
  );
};

export default GroupDetails;
